import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { map, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { SubscriptionService } from './subscription.service';
import { AuthService } from '../../auth/services/auth/auth.service';

/**
 * Central reactive state hub for trial expiry restrictions.
 * Derives all state from SubscriptionService.companySubscriptionDetails.
 *
 * Uses BehaviorSubjects internally to guarantee late subscribers always
 * receive the latest value - shareReplay can miss updates when the source
 * emits before anyone subscribes downstream.
 *
 * Phases:
 *   'Active'       -> Full access, paid subscription
 *   'Trial'        -> Full access, trial ongoing
 *   'GracePeriod'  -> Full access, trial expired but within grace period (day 0-14)
 *   'Restricted'   -> Read-only, write operations blocked (day 15-44)
 *   'Suspended'    -> Fully locked except whitelist (day 45+)
 */
@Injectable({ providedIn: 'root' })
export class RestrictedModeService implements OnDestroy {

  private destroy$ = new Subject<void>();

  private phaseSubject = new BehaviorSubject<string>('Active');
  private daysSubject = new BehaviorSubject<number>(0);
  private shouldShowBannerSubject = new BehaviorSubject<boolean>(false);

  /** Current subscription phase */
  subscriptionPhase$: Observable<string> = this.phaseSubject.asObservable();

  /** True when phase is 'Restricted' or 'Suspended' */
  isRestricted$: Observable<boolean>;

  /** True when phase is 'Suspended' */
  isSuspended$: Observable<boolean>;

  /** True when phase is 'Trial' (active trial, not expired) */
  isTrialActive$: Observable<boolean>;

  /** True when phase is 'GracePeriod' */
  isGracePeriod$: Observable<boolean>;

  /** True when user can perform write operations (create, edit, delete) */
  canPerformWrite$: Observable<boolean>;

  /** Days until the next restriction phase */
  daysUntilNextPhase$: Observable<number> = this.daysSubject.asObservable();

  /** True when the trial banner should be shown (for admins/teamleaders, not terminals) */
  shouldShowBanner$: Observable<boolean> = this.shouldShowBannerSubject.asObservable();

  constructor(
    private subscriptionService: SubscriptionService,
    private authService: AuthService
  ) {
    // Drive phase from subscription details
    this.subscriptionService.companySubscriptionDetails.pipe(
      takeUntil(this.destroy$)
    ).subscribe(d => {
      const phase = d?.Phase || 'Active';
      const days = d?.DaysUntilNextPhase || 0;
      this.phaseSubject.next(phase);
      this.daysSubject.next(days);
    });

    // Ensure subscription details are loaded (AuthGuard may skip loading if settings are cached)
    this.authService.authState$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.subscriptionService.loadCompanyFeatures().subscribe();
      }
    });

    // Drive banner visibility from phase + auth state
    combineLatest([
      this.subscriptionPhase$,
      this.authService.authState$
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([phase, _isAuthenticated]) => {
      let show = false;
      if (!this.authService.isTerminal && this.authService.isManegeOrTeamLeader) {
        show = phase === 'Trial' || phase === 'GracePeriod' || phase === 'Restricted';
      }
      this.shouldShowBannerSubject.next(show);
    });

    // Derived observables from phaseSubject
    this.isRestricted$ = this.subscriptionPhase$.pipe(
      map(phase => phase === 'Restricted' || phase === 'Suspended'),
      distinctUntilChanged()
    );

    this.isSuspended$ = this.subscriptionPhase$.pipe(
      map(phase => phase === 'Suspended'),
      distinctUntilChanged()
    );

    this.isTrialActive$ = this.subscriptionPhase$.pipe(
      map(phase => phase === 'Trial'),
      distinctUntilChanged()
    );

    this.isGracePeriod$ = this.subscriptionPhase$.pipe(
      map(phase => phase === 'GracePeriod'),
      distinctUntilChanged()
    );

    this.canPerformWrite$ = this.subscriptionPhase$.pipe(
      map(phase => phase !== 'Restricted' && phase !== 'Suspended'),
      distinctUntilChanged()
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
